import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Avatar, Box, Drawer, Link, Typography } from '@mui/material';
import useResponsive from '../../hooks/useResponsive';
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import { adminLevel } from 'src/axiosLib/services/institution/admin';
import { get } from 'lodash';
import { getNavElementsForAdmin } from '../../util/helpers';

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: 14,
  background: 'rgba(99, 102, 241, 0.08)',
  border: '1px solid rgba(99, 102, 241, 0.12)',
}));

// No manual path remapping needed — getNavElementsForAdmin reads
// dashboardMode from localStorage and returns correct paths automatically.

CoolDashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function CoolDashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');
  const [adminData, setAdminData] = useState({});

  useEffect(() => {
    if (isOpenSidebar) onCloseSidebar();
  }, [pathname]);

  useEffect(() => {
    adminLevel((error, data) => {
      if (!error) setAdminData(get(data, 'admin'));
    });
  }, []);

  const coolNavConfig = getNavElementsForAdmin(get(adminData, 'roleSet', []));

  const drawerContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Logo area */}
      <Box sx={{ px: 2.5, py: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Logo />
        <Typography sx={{
          fontWeight: 800, fontSize: '1.1rem',
          background: 'linear-gradient(135deg, #6366f1, #a78bfa)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          Medi Inn
        </Typography>
      </Box>

      {/* Admin info */}
      <Box sx={{ mb: 2, mx: 2.5 }}>
        <AccountStyle>
          <Avatar
            src={'https://ui-avatars.com/api/?name=' + get(adminData, 'username', 'R') + '&size=128&background=6366f1&color=fff'}
            alt="photoURL"
            sx={{ width: 44, height: 44, border: '2px solid rgba(99,102,241,0.3)' }}
          />
          <Box sx={{ ml: 1.5, overflow: 'hidden' }}>
            <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 700 }} noWrap>
              {get(adminData, 'username', '').substring(0, 15)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              {get(adminData, 'phone', '').substring(0, 15)}
            </Typography>
          </Box>
        </AccountStyle>
      </Box>

      {/* Institution info */}
      <Box sx={{ mb: 2, mx: 2.5 }}>
        <Box sx={{
          p: 2, borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.06))',
          border: '1px solid rgba(99,102,241,0.08)',
        }}>
          <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
            Institution
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, mt: 0.5 }} noWrap>
            {get(adminData, 'institutionId.institutionName', '')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.3 }} noWrap>
            📍 {get(adminData, 'institutionId.address', '').replaceAll('$$$', ' ')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
            📞 {get(adminData, 'institutionId.mobile', '').replaceAll('$$$', ' ')}
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <NavSection navConfig={coolNavConfig} />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  const drawerPaperStyle = {
    width: DRAWER_WIDTH,
    background: 'linear-gradient(180deg, #fefefe 0%, #f8f7ff 100%)',
    borderRight: '1px solid rgba(99, 102, 241, 0.08)',
  };

  return (
    <RootStyle>
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar} PaperProps={{ sx: drawerPaperStyle }}>
          {drawerContent}
        </Drawer>
      )}
      {isDesktop && (
        <Drawer open variant="persistent" PaperProps={{ sx: drawerPaperStyle }}>
          {drawerContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
